// noinspection GraphQLUnresolvedReference

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import { invalidateQuery, type InvalidateQueryOptions, useApiQuery } from '../../../../base/api/queryUtils.ts'
import type { QueryOpts } from '../../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../../utils/gql.ts'
import { impresoraAsignacionFragmentAst } from '../fragments/impresoraFragment.ts'
import { ImpresoraAsignacionProps } from '../interfaces/impresora.ts'

const query = /* GraphQL */ gqlAst`
  ${impresoraAsignacionFragmentAst}

  query IMPRESORA_ASIGNACION_LISTADO($codigoSucursal: Int!) {
    impresoraAsignacionListado(codigoSucursal: $codigoSucursal) {
      ...impresoraAsignacionFields
    }
  }
`

export const KEY_IMPRESORA_ASIGNACION_LISTADO = 'impresora-asignacion-listado'

export interface ApiImpresoraAsignacionListadoParams {
  codigoSucursal: number
}

/**
 * Listado de asignaciones de impresoras (documento, formato y copias) de una sucursal.
 * @param params Código de sucursal de la sesión.
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @author isi-template
 */
export const apiImpresoraAsignacionListado = async (
  params: ApiImpresoraAsignacionListadoParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ImpresoraAsignacionProps[]> => {
  try {
    const data = await graphqlClient.request<{ impresoraAsignacionListado: ImpresoraAsignacionProps[] }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.impresoraAsignacionListado
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Invalida el listado de asignaciones desde cualquier lugar (ej. onSuccess de mutaciones),
 * sin necesidad de tener montado el hook.
 * @example invalidateImpresoraAsignacionListado()
 * @example invalidateImpresoraAsignacionListado({ extraQueryKeys: [codigoSucursal] })
 * @author isi-template
 */
export const invalidateImpresoraAsignacionListado = (options?: InvalidateQueryOptions) =>
  invalidateQuery(KEY_IMPRESORA_ASIGNACION_LISTADO, options)

/**
 * Hook para consultar las asignaciones de impresoras de una sucursal.
 * queryKey: [KEY_IMPRESORA_ASIGNACION_LISTADO, codigoSucursal, ...(options.queryKeys ?? [])]
 * Para invalidar sin montar el hook, usar `invalidateImpresoraAsignacionListado()`.
 * @param params Código de sucursal de la sesión.
 * @param options Opciones de React Query con soporte de queryKeys adicionales.
 * @author isi-template
 */
export const useImpresoraAsignacionListado = (
  params: ApiImpresoraAsignacionListadoParams,
  options: QueryOpts<ImpresoraAsignacionProps[]> = {},
) =>
  useApiQuery(
    KEY_IMPRESORA_ASIGNACION_LISTADO,
    (ctx) => apiImpresoraAsignacionListado(params, { signal: ctx.signal }),
    {
      refetchOnWindowFocus: false,
      ...options,
      queryKeys: [params.codigoSucursal, ...(options.queryKeys ?? [])],
    },
  )
